import { Bell, Menu, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/stores/ui.store';

interface TopBannerProps {
  title: string;
  subtitle?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (v: string) => void;
}

export function TopBanner({
  title,
  subtitle,
  searchPlaceholder = 'Buscar usuario casino...',
  searchValue,
  onSearchChange,
}: TopBannerProps) {
  const { user, logout } = useAuth();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const initials = (user?.display_name || user?.username || '??').slice(0, 2).toUpperCase();

  return (
    <header className="flex h-16 items-center gap-4 border-b border-border bg-background px-6">
      <button
        type="button"
        onClick={toggleSidebar}
        aria-label="Abrir menú"
        className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-muted md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      <div className="relative w-72 max-w-full">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder={searchPlaceholder}
          value={searchValue ?? ''}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
      </div>

      <Button variant="ghost" size="icon" title="Notificaciones">
        <Bell className="h-5 w-5" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Avatar>
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            <div className="text-sm font-semibold">{user?.display_name || user?.username || 'Invitado'}</div>
            <div className="text-xs text-muted-foreground">{user?.email || user?.role || '—'}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>Mi perfil</DropdownMenuItem>
          <DropdownMenuItem disabled>Preferencias</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void logout()}>Cerrar sesión</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
